// --- React and Redux
import React, {useState} from 'react';
import {useSelector} from 'react-redux';
// --- UI Components
import {Col, Row} from 'antd';
import Typography from '@dtci/spellbook/Typography';
import Select from '@dtci/spellbook/Select';
import Button from '@dtci/spellbook/Button';
import Input from '@dtci/spellbook/Input';
import {InputLabel} from '../../../InputLabel/InputLabel';
import {EditorComponent} from './editor';
// --- Styles
import './styles.scss';
// --- Utilities
import moment from 'moment';
import {commentTypes, getCommentTypeName} from '../../../../utils/commentType';
import {filterCriteria} from '../../../../utils/filterCriteria';
// --- API
import {datastageApi} from '../../../../api';
// --- Hooks
import {useDeleteDialog} from '../../../dialogs/dialogs/delete';
import {useLoading} from '../../../../utils/hooks/useLoading';
import {useCommentsActions} from '../../../../store/comments/useComments';
import {useUploadDialog} from '../../../dialogs/dialogs/upload';
import {saveAs} from 'file-saver';
import {useToastNotification} from '../../../ToastNotifications';
import {startJobStatusTracker} from '../../../../utils/jobStatusTracker';
import {usePermissions} from '../../../../utils/permissions';

/**
 * EditCommentForm Component
 *
 * This component is responsible for rendering the form used to edit comments.
 * It includes fields for the comment title, type, and body.
 * It also allows users to toggle between public and private comments.
 *
 * @component
 * @returns {JSX.Element} The rendered EditCommentForm component.
 */
export function EditCommentForm() {
    const {openUploadDialog} = useUploadDialog();
    const {openDeleteDialog} = useDeleteDialog();
    const {setLoadingTrue, setLoadingFalse} = useLoading();
    const {updateComments, updateCommentsFilterForm} = useCommentsActions();
    const {openToast} = useToastNotification();
    const {comment, commentProduct, filterForm, commentRange, commentSort, isCreate} = useSelector(
        (s) => s.comments,
    );
    const [isPublic, setIsPublic] = useState(comment?.isPublic);
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const {isLegalAdmin, isProductCommentsAdmin} = usePermissions();

    // Function to handle the action to delete attachments
    const onOpenDeleteDialog = (data) => {
        openDeleteDialog(
            'Yes, Delete',
            [
                `Are you sure you want to delete ${data?.attachmentName}`,
                'Please note: This action can not be reversed.',
            ],
            async () => {
                setLoadingTrue();
                try {
                    const res = await datastageApi.deleteAttachment(data.id, true);

                    if (res?.success === true) {
                        await delay(5000);
                        const openComment = await datastageApi.commentById(data.productCommentId);
                        const commentlist = await datastageApi.comments(
                            filterCriteria(filterForm, commentProduct, 'comments'),
                            commentRange,
                            commentSort,
                        );

                        updateComments({
                            comment: openComment,
                            comments: commentlist?.content,
                        });

                        openToast('Attachment removed successfully', 'success');
                    }
                } catch (error) {
                    console.error('Error removing attachment:', error);
                    openToast(
                        'Error removing attachment',
                        'error',
                    );
                } finally {
                    setLoadingFalse();
                }
            },
        );
    };

    // Function to handle the action to upload attachments
    const onOpenUploadDialog = () => {
        openUploadDialog(comment, async ({file}) => {
            if (!file) return;

            const formData = new FormData();
            file.forEach((rawFile) => {
                formData.append('files', rawFile);
            });
            formData.append('productCommentId', comment.id);

            try {
                setLoadingTrue();
                const res = await datastageApi.uploadAttachment(formData);
                startJobStatusTracker(res?.id, async (message, status) => {
                    openToast(message, status);

                    if (status === 'success') {
                        const commentList = await datastageApi.comments(
                            filterCriteria(filterForm, commentProduct, 'comments'),
                            commentRange,
                            commentSort
                        );
                        const openComment = await datastageApi.commentById(comment.id);

                        updateComments({
                            comments: commentList?.content,
                            comment: openComment,
                            isAttachmentsModalOpen: status === 'success' && false,
                            isCreate: false,
                            isEditModalOpen: true,
                        });
                    }

                    setLoadingFalse();
                });

                setLoadingFalse();
            } catch (error) {
                console.error('Error uploading attachment:', error);
                openToast('Error uploading attachment', 'error');
                setLoadingFalse();
            }
        });
    };

    // Function to handle the action to toggle between public and private comments
    const togglePublicPrivate = () => {
        const newState = !isPublic;
        setIsPublic(newState);
        updateCommentsFilterForm({
            key: 'comment',
            data: {public: newState},
        });
    };

    const handleExport = async () => {
        const {clearanceMemoTitleId, id} = comment;

        setLoadingTrue();
        const res = await datastageApi.exportComment(clearanceMemoTitleId, id)
        saveAs(res.data, 'download.pdf');

        setLoadingFalse();

    };


    const downloadAttachment = async (rightsLineId, fileName) => {
        setLoadingTrue();
        const res = await datastageApi.downloadAttachment(rightsLineId, fileName);
        saveAs(res.data, fileName);
        setLoadingFalse();
    };

    return (
        <>
            <div className="edit-comment-toolbar">
                {(isLegalAdmin || isProductCommentsAdmin) && <Button
                    iconType={`fa-solid ${isPublic ? 'fa-eye' : 'fa-lock'}`}
                    labelText={isPublic ? 'Public' : 'Private'}
                    styleType="link"
                    onClick={togglePublicPrivate}
                >
                    {isPublic ? 'Public' : 'Private'}
                </Button>}
                {(!isCreate && (isLegalAdmin || isProductCommentsAdmin)) && (
                    <Button
                        iconType="fa-solid fa-upload"
                        labelText="Upload Attachments"
                        styleType="link"
                        onClick={onOpenUploadDialog}
                    >
                        Upload Attachments
                    </Button>
                )}
                <Button
                    iconType="fa-solid fa-file-pdf"
                    labelText="PDF"
                    styleType="link"
                    onClick={handleExport}
                >
                    PDF
                </Button>
            </div>

            <div className="edit-comment-row">
                <div className="edit-comment-title">
                    <InputLabel label="Comment Title">
                        <Input
                            disabled={!isLegalAdmin && !isProductCommentsAdmin}
                            value={comment.title}
                            onChange={(e) =>
                                updateCommentsFilterForm({
                                    key: 'comment',
                                    data: {title: e.target.value},
                                })
                            }
                            width="auto"
                            id="title"
                            name="title"
                            placeholder="Enter"
                        />
                    </InputLabel>
                </div>
                <div className="edit-comment-meta">
                    <Typography variant="T30" className="edit-comment-meta-text">
                        {comment.modifiedBy?.fullName}
                    </Typography>
                </div>
                <div className="edit-comment-meta">
                    <Typography variant="T30" className="edit-comment-meta-text">
                        {comment.modifiedAt &&
                            moment(comment.modifiedAt.toString().substring(0, 10)).format(
                                'YYYY-MM-DD',
                            )}
                    </Typography>
                </div>
            </div>

            <div className="edit-comment-row">
                <div className="edit-comment-type">
                    <Select
                        onChange={(value) =>
                            updateCommentsFilterForm({
                                key: 'comment',
                                data: {commentType: value?.value},
                            })
                        }
                        name="commentType"
                        options={commentTypes}
                        isClearable
                        isSearchable
                        customClear={false}
                        disabled={!isLegalAdmin && !isProductCommentsAdmin}
                        value={{
                            label: getCommentTypeName(comment.commentType),
                            value: comment.commentType,
                        }}
                    />
                </div>
            </div>

            <div className="edit-comment-row">
                <EditorComponent isReadOnly={!isLegalAdmin && !isProductCommentsAdmin}/>
            </div>

            {comment?.attachments && (
                <div className="edit-comment-attachments">
                    {comment.attachments.map((doc) => (
                        <div key={doc.id || doc.attachmentName} className="edit-comment-attachment">
                            <a
                                target="_blank"
                                rel="noreferrer"
                                className="edit-comment-attachment-link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    downloadAttachment(doc?.rightslineId, doc?.attachmentName)
                                }}>
                                {doc.attachmentName}
                            </a>
                            {(isLegalAdmin || isProductCommentsAdmin) && <Button
                                iconType="fa-regular fa-trash"
                                sizeType="large"
                                styleType="link"
                                onClick={() => onOpenDeleteDialog(doc)}
                            />}
                            <br/>
                            <br/>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}
